import React, { ChangeEvent, useCallback, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, LinearProgress, Popover, Typography } from "@mui/material";

import "./Logo.styles.scss";
import { AsyncContent, Loading } from "components";
import LogoPlaceholder from "../../../assets/icons/deal-logo-placeholder.svg?url";
import { useDealActions, useDealIdSelector, useDealLogoSelec, useLogoToSaveSelect } from "store";
import { getPercentage } from "utils/files";
import { useActionHook } from "hooks";
import { AxiosProgressEvent } from "axios";

export const Logo: React.FC = () => {
  const { uploadLogoAction, saveLogoAction, deleteLogoAction } = useDealActions();
  const logo = useDealLogoSelec();
  const dealId = useDealIdSelector();
  const logoToSave = useLogoToSaveSelect();

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const [isSpinner, setIsSpinner] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [controller, setController] = useState<AbortController | null>(null);

  const { action, loading } = useActionHook(uploadLogoAction);

  const isLogoCanBeSaved: boolean = useMemo(() => {
    return Boolean(logoToSave && logoToSave.file_url && logoToSave.state === "verified");
  }, [logoToSave]);

  const inputRef = useRef<HTMLInputElement>(null);

  const onUploadProgress = useCallback((progressEvent: AxiosProgressEvent) => {
    const { total, loaded } = progressEvent;
    if (!total) return;
    setProgress(getPercentage(loaded, total));
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        if (!dealId) return;
        const newController = new AbortController();
        setController(newController);

        setIsSpinner(true);
        await action({
          dealId,
          onUploadProgress,
          file,
          signal: newController?.signal
        });
      } catch (error) {
        throw error;
      } finally {
        setController(null);
      }
    },
    [dealId]
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onFileChoose = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.length) {
        const file = event.target.files[0];
        uploadFile(file);
      }
    },
    [uploadFile]
  );

  const onSave = useCallback(async () => {
    try {
      if (isLogoCanBeSaved && logoToSave && dealId) {
        setIsSpinner(true);
        await saveLogoAction({ dealId, fileId: logoToSave.id });
      }
      if (isDeleting && logo) {
        setIsSpinner(true);
        await deleteLogoAction({ logoId: logo.id, isCurrentLogo: true });
        setIsDeleting(false);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSpinner(false);
      setProgress(0);
      handleClose();
    }
  }, [isLogoCanBeSaved, logoToSave, dealId, isDeleting, logo]);

  const onCancel = useCallback(async () => {
    try {
      if (isDeleting) {
        setIsDeleting(false);
      }
      if (controller) {
        controller.abort();
        setProgress(0);
        setIsSpinner(false);
        setController(null);
      }
      if (isLogoCanBeSaved && logoToSave) {
        await deleteLogoAction({ logoId: logoToSave.id });
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSpinner(false);
      setProgress(0);
      handleClose();
    }
  }, [controller, isLogoCanBeSaved, isSpinner, isDeleting, logoToSave]);

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <>
      <div
        //the logic for working with the logo has been moved to NavBar component
        // onClick={handleClick}
        className="deal-header__logo-simple"
      >
        <AsyncContent loading={isSpinner} spinnerProps={{ size: 25 }}>
          <img height="36px" src={logo?.file_url || LogoPlaceholder} />
        </AsyncContent>
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        <div className="edit-logo-popup">
          <div className="head">
            <span className="title">Edit Logo</span>
            <IconButton size="sm" onClick={handleClose}>
              <CloseIcon fontSize="md" />
            </IconButton>
          </div>
          <div className="body">
            {/* Original logo if it is */}
            {logo && !loading && !logoToSave && !isDeleting && (
              <div className="file-holder file-holder--space">
                <span className="name">{logo?.name}</span>
                <IconButton sx={{ backgroundColor: "#dae0e2" }} size="sm" onClick={() => setIsDeleting(true)}>
                  <CloseIcon fontSize="md" />
                </IconButton>
              </div>
            )}
            {!(loading || logoToSave) ? (
              <div className="input-block">
                <div
                  onClick={() => {
                    if (inputRef.current) {
                      inputRef.current.click();
                    }
                  }}
                  className="trigger"
                >
                  Browse Image
                  <input ref={inputRef} onChange={onFileChoose} className="input" type="file" />
                </div>
              </div>
            ) : (
              <>
                <div className="file-holder">
                  {loading ? (
                    <Loading loading={loading} />
                  ) : (
                    <>
                      <span className="name">{logoToSave?.name}</span>
                      <IconButton
                        sx={{ backgroundColor: "#dae0e2" }}
                        size="sm"
                        disabled={!isLogoCanBeSaved}
                        onClick={onCancel}
                      >
                        <CloseIcon fontSize="md" />
                      </IconButton>
                    </>
                  )}
                </div>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ width: "100%", mr: 1 }}>
                    <LinearProgress variant="determinate" value={progress} />
                  </Box>
                  <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body2" color="text.secondary">{`${Math.round(progress)}%`}</Typography>
                  </Box>
                </Box>
              </>
            )}
          </div>
          <div className="footer">
            <Button onClick={onCancel} variant="outlined">
              Cancel
            </Button>
            <Button disabled={!(isLogoCanBeSaved || isDeleting)} onClick={onSave} variant="contained">
              Save
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};
